import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Container,
  Paper,
  Typography,
  TextField,
  IconButton,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Chip,
  Tooltip,
  FormHelperText,
  Autocomplete,
  Alert,
  Switch,
  FormControlLabel,
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DateRangeIcon from "@mui/icons-material/DateRange";
import AddIcon from "@mui/icons-material/Add";
import { Link, Link as RouterLink } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ThunkDispatch } from "@reduxjs/toolkit";
import { format } from "date-fns";

import { RootState } from "../../store/Store";
import {
  fetchData,
  postData,
  putData,
  deleteData,
  FetchDataSuccessAction,
  FetchDataFailureAction,
} from "../../store/actions";
import {
  ToastVariant,
  useCustomToast,
} from "../../components/Methods/SnackBar";
import Navbar from "../../components/Page Component/Navbar";
import Footer from "../../components/Page Component/Footer";
import PageTitle from "../../components/Page Component/PageTitle";
import { setIsLoading } from "../../store/LoadingSlice";
import {
  processComplianceAssignmentsUrl,
  processComplianceTemplatesUrl,
  projectsUrl,
  projectManagersUrl,
  bulkAssignComplianceUrl,
} from "../../Service/Apis";
import CustomNoRowsOverlay from "../../components/Data/NoRowsOverlayComponent";
import { getDataGridPaginationProps } from "utils/paginationConfig";
import { titleCase } from "./utils";
import axios from "axios";

interface Template {
  template_id: string;
  template_name: string;
  template_data: {
    questions: { id: string; question: string }[];
    sprintCount: number;
    sprintNames?: { id: string; name: string }[];
  };
  is_active: boolean;
}

interface ProjectManager {
  id: string;
  name: string;
  email: string;
}

interface Project {
  project_id: string;
  project_name: string;
  project_key: string;
  team_id: string;
  team_name: string;
  project_manager: string | null;
  manager_name: string | null;
}

interface ComplianceAssignment {
  assignment_id: string;
  project: string;
  project_name: string;
  template: string;
  template_name: string;
  assigned_to: string;
  assigned_to_name: string;
  assigned_by: string;
  assigned_by_name: string;
  due_date: string;
  due_date_str: string;
  completion_date: string | null;
  status: "pending" | "in_progress" | "completed" | "overdue";
  month_year: string;
  month_year_str: string;
}

const ProcessComplianceAssignments: React.FC = () => {
  const [assignments, setAssignments] = useState<ComplianceAssignment[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [managers, setManagers] = useState<ProjectManager[]>([]);
  const [customDueDate, setCustomDueDate] = useState<Date | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState<Date | null>(new Date());
  const [formData, setFormData] = useState({
    template_id: "",
    project_ids: [] as string[],
    month_year: format(new Date(), "yyyy-MM"),
    manager_overrides: {} as Record<string, string>,
    default_manager_id: "",
    custom_due_date: "",
  });

  // Count projects without managers
  const [projectsWithoutManager, setProjectsWithoutManager] = useState<
    Project[]
  >([]);

  const showToast = useCustomToast();
  const dispatch = useDispatch();
  const dispatch2: ThunkDispatch<
    RootState,
    null,
    FetchDataSuccessAction | FetchDataFailureAction
  > = useDispatch();

  const selectedRole = useSelector(
    (state: RootState) => state.auth.selectedRole
  );

  useEffect(() => {
    fetchAssignments();
    fetchTemplates();
    fetchProjects();
    fetchManagers();
  }, []);

  useEffect(() => {
    // Check for projects without managers
    const withoutManager = projects.filter(
      (p) =>
        formData.project_ids.includes(p.project_id) &&
        !p.project_manager &&
        !formData.manager_overrides[p.project_id]
    );
    setProjectsWithoutManager(withoutManager);
  }, [formData.project_ids, formData.manager_overrides, projects]);

  const fetchAssignments = async () => {
    try {
      dispatch(setIsLoading(true));
      const response = await dispatch2(
        fetchData(`${processComplianceAssignmentsUrl}?role=${selectedRole}`)
      );
      setAssignments(response as ComplianceAssignment[]);
    } catch (error) {
      console.error("Error fetching assignments:", error);
      showToast("Error fetching assignments", "error", true);
    } finally {
      dispatch(setIsLoading(false));
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await dispatch2(
        fetchData(`${processComplianceTemplatesUrl}?role=${selectedRole}`)
      );
      // Filter only active templates
      setTemplates(
        (response as Template[]).filter((template) => template.is_active)
      );
    } catch (error) {
      console.error("Error fetching templates:", error);
      showToast("Error fetching templates", "error", true);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await dispatch2(
        fetchData(`${projectsUrl}?role=${selectedRole}`)
      );
      setProjects(response as Project[]);
    } catch (error) {
      console.error("Error fetching projects:", error);
      showToast("Error fetching projects", "error", true);
    }
  };

  const fetchManagers = async () => {
    try {
      const response = await dispatch2(
        fetchData(`${projectManagersUrl}?role=${selectedRole}`)
      );
      setManagers(response as ProjectManager[]);
    } catch (error) {
      console.error("Error fetching managers:", error);
      showToast("Error fetching managers", "error", true);
    }
  };

  const handleOpenDialog = () => {
    setFormData({
      template_id: "",
      project_ids: [],
      month_year: format(new Date(), "yyyy-MM"),
      manager_overrides: {},
      default_manager_id: "",
      custom_due_date: "",
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleProjectChange = (selectedProjects: Project[]) => {
    const projectIds = selectedProjects.map((project) => project.project_id);
    setFormData({
      ...formData,
      project_ids: projectIds,
    });
  };

  const handleTemplateChange = (e: any) => {
    setFormData({
      ...formData,
      template_id: e.target.value,
    });
  };

  const handleDueDateChange = (date: Date | null) => {
    if (date) {
      setCustomDueDate(date);
      setFormData({
        ...formData,
        custom_due_date: format(date, "yyyy-MM-dd"),
      });
    } else {
      setCustomDueDate(null);
      setFormData({
        ...formData,
        custom_due_date: "",
      });
    }
  };

  const handleMonthChange = (date: Date | null) => {
    if (date) {
      setSelectedMonth(date);
      setFormData({
        ...formData,
        month_year: format(date, "yyyy-MM"),
      });
    }
  };

  const handleManagerOverride = (projectId: string, managerId: string) => {
    setFormData({
      ...formData,
      manager_overrides: {
        ...formData.manager_overrides,
        [projectId]: managerId,
      },
    });
  };

  const handleDefaultManagerChange = (e: any) => {
    setFormData({
      ...formData,
      default_manager_id: e.target.value,
    });
  };

  const handleSubmit = async () => {
    try {
      dispatch(setIsLoading(true));

      if (!formData.template_id) {
        showToast("Please select a template", "error", true);
        dispatch(setIsLoading(false));
        return;
      }

      if (formData.project_ids.length === 0) {
        showToast("Please select at least one project", "error", true);
        dispatch(setIsLoading(false));
        return;
      }

      // Check if any projects are missing managers and no default is set
      if (projectsWithoutManager.length > 0 && !formData.default_manager_id) {
        showToast(
          "Please select a default manager for projects without assigned managers",
          "error",
          true
        );
        dispatch(setIsLoading(false));
        return;
      }

      const response: any = await axios.post(
        `${bulkAssignComplianceUrl}?role=${selectedRole}`,
        formData,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      console.log("Response:", response);
      if (response && response.data && response.data.success) {
        showToast(
          response.data.message || "Assignments created successfully",
          "success",
          true
        );
        // Show info about skipped projects if any
        if (
          response.data.skipped_projects &&
          response.data.skipped_projects.length > 0
        ) {
          // Extract just the project IDs/names from the error messages
          const skippedProjectNames = response.data.skipped_projects.map(
            (skip: string) => {
              // Extract the project ID part before any error message
              const match = skip.match(/^([^ ]+)/);
              return match ? match[0] : skip;
            }
          );

          showToast(
            `Some projects were skipped: ${skippedProjectNames.join(", ")}`,
            "info",
            true
          );

          // Log the full error details to console for debugging
          console.log(
            "Skipped projects details:",
            response.data.skipped_projects
          );
        }

        handleCloseDialog();
        fetchAssignments();
      }
    } catch (error) {
      console.error("Error creating assignments:", error);
      showToast("Error creating assignments", "error", true);
    } finally {
      dispatch(setIsLoading(false));
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "completed":
        return "#4caf50";
      case "in_progress":
        return "#2196f3";
      case "pending":
        return "#ff9800";
      case "overdue":
        return "#f44336";
      default:
        return "#757575";
    }
  };

  const columns: GridColDef[] = [
    {
      field: "sr_no",
      headerName: "Sr No.",
      width: 80,
      valueGetter: (params) => {
        const index = assignments.findIndex(
          (a) => a.assignment_id === params.row.assignment_id
        );
        return index + 1;
      },
    },
    {
      field: "project_name",
      headerName: "Project",
      flex: 1,
    },
    {
      field: "template_name",
      headerName: "Template",
      width: 200,
    },
    {
      field: "month_year_str",
      headerName: "Period",
      width: 120,
    },
    {
      field: "assigned_to_name",
      headerName: "Assigned To",
      width: 180,
    },
    {
      field: "due_date",
      headerName: "Due Date",
      width: 120,
      valueFormatter: (params) => {
        if (!params.value) return "-";
        const [year, month, day] = params.value.split("-");
        return `${day}/${month}/${year}`;
      },
    },
    {
      field: "status",
      headerName: "Status",
      width: 120,
      renderCell: (params) => (
        <Chip
          label={titleCase(params.value)}
          size="small"
          sx={{
            backgroundColor: getStatusColor(params.value),
            color: "white",
          }}
        />
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      renderCell: (params) => (
        <Box>
          <Tooltip title="View/Edit Form">
            <Link to={`/process-compliance-form/${params.row.assignment_id}`}>
              <IconButton color="primary" size="small">
                {params.row.status === "completed" ? (
                  <VisibilityIcon />
                ) : (
                  <EditIcon />
                )}
              </IconButton>
            </Link>
          </Tooltip>
        </Box>
      ),
    },
  ];

  // Get selected projects with their data
  const selectedProjects = projects.filter((project) =>
    formData.project_ids.includes(project.project_id)
  );

  return (
    <>
      <Container maxWidth="lg" sx={{ mt: 12, mb: 10 }}>
        <Paper elevation={3} sx={{ p: 2, borderRadius: 2 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={3}
          >
            <PageTitle>Process Compliance Assignments</PageTitle>
            {(selectedRole === "HR" || selectedRole === "Admin") && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleOpenDialog}
              >
                Create Assignments
              </Button>
            )}
          </Box>

          <DataGrid
            rows={assignments}
            columns={columns}
            getRowId={(row) => row.assignment_id}
            autoHeight
            {...getDataGridPaginationProps()}
            disableRowSelectionOnClick
            slots={{
              noRowsOverlay: CustomNoRowsOverlay,
            }}
          />
        </Paper>
      </Container>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Create Compliance Assignments</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="template-select-label">Template</InputLabel>
                <Select
                  labelId="template-select-label"
                  id="template-select"
                  value={formData.template_id}
                  label="Template"
                  onChange={handleTemplateChange}
                >
                  <MenuItem value="">
                    <em>Select a template</em>
                  </MenuItem>
                  {templates.map((template) => (
                    <MenuItem
                      key={template.template_id}
                      value={template.template_id}
                    >
                      {template.template_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Month & Year"
                  value={selectedMonth}
                  onChange={handleMonthChange}
                  views={["month", "year"]}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      variant: "outlined",
                    },
                  }}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={12}>
              <Autocomplete
                multiple
                id="projects-select"
                options={projects}
                getOptionLabel={(option) => option.project_name}
                isOptionEqualToValue={(option, value) =>
                  option.project_id === value.project_id
                }
                onChange={(_, value) => handleProjectChange(value as any)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Projects"
                    placeholder="Projects"
                    variant="outlined"
                  />
                )}
              />
              <FormHelperText>
                Select projects to assign for compliance
              </FormHelperText>
            </Grid>

            {/* Default Manager Section */}
            {projectsWithoutManager.length > 0 && (
              <Grid item xs={12}>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {projectsWithoutManager.length} selected project(s) don't have
                  assigned managers. Please select a manager below.
                </Alert>

                <FormControl fullWidth>
                  <InputLabel id="default-manager-label">
                    Previous selected Manager
                  </InputLabel>
                  <Select
                    labelId="default-manager-label"
                    id="default-manager-select"
                    value={formData.default_manager_id}
                    label="Default Manager"
                    onChange={handleDefaultManagerChange}
                  >
                    <MenuItem value="">
                      <em>Select a default manager</em>
                    </MenuItem>
                    {managers.map((manager) => (
                      <MenuItem key={manager.id} value={manager.id}>
                        {manager.name}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    This manager will be assigned to any projects that don't
                    have a manager
                  </FormHelperText>
                </FormControl>
              </Grid>
            )}

            {/* Project Manager Section */}
            {selectedProjects.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  Project Managers
                </Typography>
                <FormHelperText sx={{ mb: 2 }}>
                  Each project's assigned manager will be responsible for the
                  compliance form. You can override the default manager if
                  needed.
                </FormHelperText>

                <Grid container spacing={2}>
                  {selectedProjects.map((project) => (
                    <Grid item xs={12} sm={6} key={project.project_id}>
                      <FormControl fullWidth size="small">
                        <InputLabel id={`manager-label-${project.project_id}`}>
                          {project.project_name}
                        </InputLabel>
                        <Select
                          labelId={`manager-label-${project.project_id}`}
                          value={
                            formData.manager_overrides[project.project_id] ||
                            project.project_manager ||
                            formData.default_manager_id ||
                            ""
                          }
                          label={project.project_name}
                          onChange={(e) =>
                            handleManagerOverride(
                              project.project_id,
                              e.target.value as string
                            )
                          }
                        >
                          <MenuItem value="">
                            <em>Select Manager</em>
                          </MenuItem>
                          {managers.map((manager) => (
                            <MenuItem key={manager.id} value={manager.id}>
                              {manager.name}
                              {manager.id === project.project_manager &&
                                " (Default)"}
                              {!project.project_manager &&
                                manager.id === formData.default_manager_id &&
                                " (Default)"}
                            </MenuItem>
                          ))}
                        </Select>
                        <FormHelperText>
                          {project.manager_name
                            ? `Default: ${project.manager_name}`
                            : formData.default_manager_id
                            ? "Using default manager"
                            : "No manager assigned"}
                        </FormHelperText>
                      </FormControl>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            )}

            {!customDueDate && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                If there is no custom due date 7th of the selected Month & Year
                will be selected as duedate
              </Alert>
            )}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!customDueDate}
                      onChange={(e) => {
                        if (!e.target.checked) {
                          handleDueDateChange(null);
                        } else {
                          // Set default to 7th of next month
                          const today = new Date();
                          const nextMonth = new Date(
                            today.getFullYear(),
                            today.getMonth() + 1,
                            7
                          );
                          handleDueDateChange(nextMonth);
                        }
                      }}
                    />
                  }
                  label="Custom Due Date"
                />
              </FormControl>
            </Grid>

            {customDueDate && (
              <Grid item xs={12} sm={6}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="Due Date"
                    value={customDueDate}
                    onChange={handleDueDateChange}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        variant: "outlined",
                        helperText:
                          "Select a custom due date for compliance forms",
                      },
                    }}
                    minDate={new Date()} // Can't set due date in the past
                  />
                </LocalizationProvider>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            Create Assignments
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ProcessComplianceAssignments;
